import { useEffect, useMemo } from "react";
import { useGetLink } from "../../services";
import { Profile, ProfileLinkConfig } from "../../client";
import ProfileWorkplace from "./ProfileWorkplace";
import ProfileProject from "./ProfileProject";
import { BulletSectionTitle } from "../../components";
import { SkillSummary } from "../dashboard/home/components/profile/SkillSummary";
import { SkillsBlock } from "../dashboard/home/components/profile/SkillsBlock";
import ProfilePersonalInfo from "./ProfilePersonalInfo";
import ProfileEducation from "./ProfileEducation";
import { ProfileSummaryContent } from "../dashboard/home/components/link-creator";
import { useStatsigClient } from "../../hooks";
import { useTheme, useThemeColors } from "../../contexts";
import { Workplace } from "../dashboard/home/components/link-creator/Workplace";

interface Props {
  profileLinkId: string;
  isEditable: boolean;
  pdfMode: boolean;
}

const ProfileContainer = (props: Props) => {
  const statsigClient = useStatsigClient();
  const { setOrganizationTheme } = useTheme();

  const { data: link } = useGetLink(props.profileLinkId, {
    enabled: !!props.profileLinkId,
  });

  const profileData = link?.accountProfile.profile_data as Profile | undefined;
  const linkConfigData = link?.accountProfile.link_config_data as
    | ProfileLinkConfig
    | undefined;

  // Set organization theme when data loads (memoized to prevent infinite loops)
  const themeData = useMemo(() => {
    if (!link.accountProfile.organization?.id) return null;
    setOrganizationTheme({
      organizationId: link.accountProfile.organization.id,
      organizationName: link.accountProfile.organization.name,
      colors: {
        // You can extract these from data or set defaults
        // For now using defaults, but you can modify based on your data structure
        primary: link.accountProfile.organization.primaryColor || "#3b82f6", // You might have data.primaryColor
        secondary: link.accountProfile.organization.secondaryColor || "#000",
        accent: "#3b82f6",
      },
      logoId: link.accountProfile.organization.logoId,
      iconLogoId: link.accountProfile.organization.iconLogoId,
    });
    return {
      organizationId: link.accountProfile.organization.id,
      organizationName: link.accountProfile.organization.name,
      colors: {
        // You can extract these from data or set defaults
        // For now using defaults, but you can modify based on your data structure
        primary: link.accountProfile.organization.primaryColor || "#3b82f6", // You might have data.primaryColor
        secondary: link.accountProfile.organization.secondaryColor || "#000",
        accent: "#3b82f6",
      },
      logoId: link.accountProfile.organization.logoId,
      iconLogoId: link.accountProfile.organization.iconLogoId,
    };
  }, [
    link.accountProfile.organization?.id,
    link.accountProfile.organization?.name,
    link.accountProfile.organization?.logoId,
    link.accountProfile.organization?.iconLogoId,
  ]);

  const { secondary } = useThemeColors();

  useEffect(() => {
    if (themeData) {
      setOrganizationTheme(themeData);
    }
  }, [themeData, setOrganizationTheme]);

  const sortedWorkplaces = useMemo(() => {
    const editedWorkplaces =
      link.accountProfile.link_config_data?.editedWorkplaces ?? [];
    const allowedIds =
      link.accountProfile.link_config_data?.profileLinkWorkplaceIds ?? [];
    const customOrder =
      link.accountProfile.link_config_data?.customWorkplaceOrder;

    // First filter and map workplaces to get edited versions if they exist
    let workplaces = profileData?.workplaces
      ? profileData.workplaces
          .filter((wp) => allowedIds.includes(wp.id))
          .map((wp) => {
            const editedWorkplace = editedWorkplaces.find(
              (w) => w.id === wp.id,
            );
            return editedWorkplace ?? wp;
          })
      : [];

    // Apply custom ordering if it exists
    if (customOrder && customOrder.length > 0) {
      const orderMap = new Map<string, number>();
      customOrder.forEach((item, index) => {
        orderMap.set(item.id, index);
      });

      // Sort workplaces based on the custom order
      workplaces = [...workplaces].sort((a, b) => {
        const orderA = orderMap.has(a.id)
          ? orderMap.get(a.id)!
          : Number.MAX_SAFE_INTEGER;
        const orderB = orderMap.has(b.id)
          ? orderMap.get(b.id)!
          : Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      });
    }

    return workplaces;
  }, [link, profileData?.workplaces]);

  const renderSectionTitle = (sectionTitle: string) => {
    if (sectionTitle === "profile_summary") {
      return linkConfigData?.language === "english"
        ? "Profile Summary"
        : "Sammanfattning";
    } else if (sectionTitle === "work_experience") {
      return linkConfigData?.language === "english"
        ? "Work experience"
        : "Erfarenhet";
    } else if (sectionTitle === "projects") {
      return linkConfigData?.language === "english" ? "Projects" : "Projekt";
    } else if (sectionTitle === "education") {
      return linkConfigData?.language === "english"
        ? "Education, courses and certificates"
        : "Utbildning, kurser och certifieringar";
    } else if (sectionTitle === "skill_stack") {
      return linkConfigData?.language === "english"
        ? "Skill stack"
        : "Kompetenser";
    } else if (sectionTitle === "highlighted_projects") {
      return linkConfigData?.language === "english"
        ? "Highlighted projects"
        : "Utvalda projekt";
    }
  };

  const sortedProjects = useMemo(() => {
    const editedSideProjects =
      link.accountProfile.link_config_data?.editedSideProjects ?? [];
    const allowedIds =
      link.accountProfile.link_config_data?.profileLinkProjects?.map(
        (p) => p.projectId,
      ) ?? [];

    return profileData?.side_projects_section?.side_projects
      ? profileData.side_projects_section.side_projects
          .filter((p) => allowedIds.includes(p.id))
          .map((wp) => {
            const editedSideProject = editedSideProjects.find(
              (w) => w.id === wp.id,
            );
            return editedSideProject ?? wp;
          })
      : [];
  }, [link, profileData?.side_projects_section?.side_projects]);

  if (!link) return null;

  const getWorkplaceName = (workplaceId: string) => {
    if (
      profileData &&
      profileData.workplaces.filter((wp) => wp.id === workplaceId).length > 0
    ) {
      return profileData.workplaces.filter((wp) => wp.id === workplaceId)[0]
        .name;
    }
    return "";
  };

  const getWorkplaceNameFromProject = (projectId: string) => {
    if (
      profileData &&
      profileData.side_projects_section &&
      profileData.side_projects_section.side_projects.filter(
        (p) => p.id === projectId,
      ).length > 0
    ) {
      let project = profileData.side_projects_section.side_projects.filter(
        (p) => p.id === projectId,
      )[0];

      return getWorkplaceName(project.workplaceId ?? "");
    }
    return "";
  };

  const skillTagsExist = () => {
    if (linkConfigData?.skillsSummaryConfig) {
      if (linkConfigData.skillsSummaryConfig.skillCategoryBlocks) {
        if (linkConfigData.skillsSummaryConfig.skillCategoryBlocks.length > 0) {
          return (
            linkConfigData.skillsSummaryConfig.skillCategoryBlocks.filter(
              (block) => block.skillTags.length > 0,
            ).length > 0
          );
        }
      }
    }
    return false;
  };

  const renderSkills = () => {
    const skillsSummaryConfig = linkConfigData?.skillsSummaryConfig;

    if (
      skillsSummaryConfig?.sortedMode !== "" &&
      skillsSummaryConfig?.skillCategoryBlocks
    ) {
      return (
        <SkillSummary
          skillCategoryBlocks={skillsSummaryConfig?.skillCategoryBlocks ?? []}
          displayIcons={skillsSummaryConfig?.displayIcons ?? true}
          pdfMode={props.pdfMode}
        />
      );
    }

    return (
      <SkillsBlock
        skillCategoryBlocks={skillsSummaryConfig?.skillCategoryBlocks ?? []}
        editable={false}
        hideIcons={!skillsSummaryConfig?.displayIcons}
        pdfMode={props.pdfMode}
      />
    );
  };

  return (
    <div>
      <ProfilePersonalInfo
        name={profileData?.name ?? ""}
        title={profileData?.title ?? ""}
        pitch={profileData?.pitch ?? ""}
        editedPitch={linkConfigData?.editedPitch}
        profilePhotoId={link?.accountProfile.profilePhotoId ?? undefined}
        isEditable={false}
        useProfilePicture={linkConfigData?.useProfilePicture ?? false}
        roundedProfilePicture={
          linkConfigData?.profileLinkPersonalInfoConfig?.roundMode ?? false
        }
        hidePersonalInfo={
          !linkConfigData?.profileLinkPersonalInfoConfig?.showPersonalInfo
        }
        useBorder={
          linkConfigData?.profileLinkPersonalInfoConfig?.useBorder ?? false
        }
        onShowCropTool={() => {}}
        onProfileUpdate={() => {}}
        isDarkModeActive={false}
        hidePitch={!linkConfigData?.profileLinkPersonalInfoConfig?.showPitch}
        updatedPhotoId={null}
        isUploadingPhoto={false}
        removePadding={false}
      />

      {linkConfigData?.profileSummaryConfig &&
        linkConfigData.profileSummaryConfig.showProfileSummary &&
        !linkConfigData.profileMissingInformation && (
          <div className="mb-6">
            <div style={{ "--text-color": secondary } as React.CSSProperties}>
              <BulletSectionTitle childrenClassName="text-[var(--text-color)]">
                {renderSectionTitle("profile_summary")}
              </BulletSectionTitle>
            </div>
            <ProfileSummaryContent
              linkConfigData={linkConfigData}
              pdfMode={props.pdfMode}
            />
          </div>
        )}

      {statsigClient?.checkGate("highlighted_projects") &&
        linkConfigData?.highlightedProjects &&
        linkConfigData?.highlightedProjects.filter((hp) => hp.included).length >
          0 && (
          <div className="mb-6">
            <div style={{ "--text-color": secondary } as React.CSSProperties}>
              <BulletSectionTitle childrenClassName="text-[var(--text-color)]">
                {renderSectionTitle("highlighted_projects")}
              </BulletSectionTitle>
            </div>
            <div className="flex flex-col gap-6 pl-3">
              {linkConfigData.highlightedProjects
                .filter((hp) => hp.included)
                .map((highlightedProject) => (
                  <ProfileWorkplace
                    key={highlightedProject.title}
                    id={highlightedProject.title}
                    isEditable={false}
                    name={
                      highlightedProject.workplaceId
                        ? getWorkplaceName(highlightedProject.workplaceId ?? "")
                        : highlightedProject.projectId
                          ? getWorkplaceNameFromProject(
                              highlightedProject.projectId,
                            )
                          : ""
                    }
                    skillTags={highlightedProject.skillTags ?? []}
                    role={highlightedProject.title ?? ""}
                    description={highlightedProject.summary}
                    isProject={true}
                    showSkills={
                      linkConfigData.highlightedProjectsConfig?.showSkills ??
                      false
                    }
                    showDescription={true}
                    onWorkplaceEdit={(description: string) => {}}
                    showSkillIcons={
                      linkConfigData.highlightedProjectsConfig?.showIcons ??
                      true
                    }
                  />
                ))}
            </div>
          </div>
        )}

      {linkConfigData?.skillsSummaryConfig &&
        linkConfigData?.skillsSummaryConfig.displaySkillsSummary &&
        linkConfigData?.skillsSummaryConfig.placeInTop === true &&
        skillTagsExist() && (
          <div className="mb-6">
            <div style={{ "--text-color": secondary } as React.CSSProperties}>
              <BulletSectionTitle childrenClassName="text-[var(--text-color)]">
                {renderSectionTitle("skill_stack")}
              </BulletSectionTitle>
            </div>
            <div className="pl-3">{renderSkills()}</div>
          </div>
        )}

      {linkConfigData?.profileLinkWorkplaceIds &&
        linkConfigData?.profileLinkWorkplaceIds.length > 0 && (
          <>
            <div style={{ "--text-color": secondary } as React.CSSProperties}>
              <BulletSectionTitle childrenClassName="text-[var(--text-color)]">
                {renderSectionTitle("work_experience")}
              </BulletSectionTitle>
            </div>
            <div className="pl-3">
              {sortedWorkplaces &&
                sortedWorkplaces.map((workplace) => (
                  <div className="mb-8" key={workplace.id}>
                    <ProfileWorkplace
                      id={workplace.id}
                      isEditable={props.isEditable}
                      name={workplace.name}
                      skillTags={workplace.skillTags}
                      role={workplace.role}
                      pdfMode={props.pdfMode}
                      description={workplace.description}
                      startDate={workplace.startDate}
                      endDate={workplace.endDate}
                      showSkillIcons={
                        linkConfigData.workplaceLinkConfig?.displaySkillIcons ??
                        true
                      }
                      showSkills={
                        linkConfigData?.workplaceLinkConfig?.displaySkills
                      }
                      showDescription={
                        linkConfigData?.workplaceLinkConfig?.displayDescription
                      }
                      onWorkplaceEdit={() => {
                        // No-op in non-editable mode
                      }}
                      onWorkplaceContentUpdate={() => {
                        // No-op in non-editable mode
                      }}
                      onProjectClickAction={() => {
                        // No-op in non-editable mode
                      }}
                    />
                  </div>
                ))}
            </div>
          </>
        )}
      {linkConfigData?.profileLinkProjects &&
        linkConfigData?.profileLinkProjects.length > 0 && (
          <>
            <div style={{ "--text-color": secondary } as React.CSSProperties}>
              <BulletSectionTitle childrenClassName="text-[var(--text-color)]">
                {renderSectionTitle("projects")}
              </BulletSectionTitle>
            </div>
            <div className="pl-3">
              {sortedProjects &&
                sortedProjects.map((project) => (
                  <div className="mb-8" key={project.id}>
                    <ProfileProject
                      id={project.id}
                      isEditable={false}
                      name={
                        project.isSideProject
                          ? "Side project"
                          : getWorkplaceName(project.workplaceId ?? "")
                      }
                      skillTags={project.skillTags}
                      title={project.title}
                      description={project.pitch}
                      showSkills={
                        linkConfigData?.projectsLinkConfig?.displaySkills
                      }
                      showSkillIcons={
                        linkConfigData?.projectsLinkConfig?.displaySkillIcons ??
                        true
                      }
                      showDescription={true}
                      onWorkplaceEdit={() => {
                        // No-op in non-editable mode
                      }}
                      onWorkplaceContentUpdate={() => {
                        // No-op in non-editable mode
                      }}
                      onProjectClickAction={() => {
                        // No-op in non-editable mode
                      }}
                      onWorkplaceNameClick={() => {
                        // No-op in non-editable mode
                      }}
                      profileId={link.accountProfile.id}
                      pdfMode={props.pdfMode}
                    />
                  </div>
                ))}
            </div>
          </>
        )}
      {linkConfigData?.displayEducationSection &&
        profileData &&
        profileData.educations &&
        profileData.educations.length > 0 && (
          <>
            <div style={{ "--text-color": secondary } as React.CSSProperties}>
              <BulletSectionTitle childrenClassName="text-[var(--text-color)]">
                {renderSectionTitle("education")}
              </BulletSectionTitle>
            </div>
            <div className="pl-3">
              {profileData?.educations &&
                profileData.educations.map((education, index) => (
                  <div className="mb-6" key={education.id}>
                    <ProfileEducation
                      isEditable={false}
                      isDarkModeActive={false}
                      key={index}
                      isBeingEdited={false}
                      isNew={false}
                      education={education}
                      onSaveItem={() => {}}
                      onCancelEdit={() => {}}
                      onRemove={() => {}}
                      onShowDialog={() => {}}
                      isLastInOrder={false}
                      onContentImprovementStarted={() => {}}
                      onContentImprovementEnded={() => {}}
                      pdfMode={false}
                      removePadding={true}
                      staticMode={true}
                      onProjectClickAction={() => {}}
                    />
                  </div>
                ))}
            </div>
          </>
        )}
      {linkConfigData?.skillsSummaryConfig &&
        linkConfigData?.skillsSummaryConfig.placeInTop === false &&
        skillTagsExist() && (
          <div className="mb-6">
            <div style={{ "--text-color": secondary } as React.CSSProperties}>
              <BulletSectionTitle childrenClassName="text-[var(--text-color)]">
                {renderSectionTitle("skill_stack")}
              </BulletSectionTitle>
            </div>
            <div className="pl-3">{renderSkills()}</div>
          </div>
        )}
    </div>
  );
};

export default ProfileContainer;
